import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Settings, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PromptPreviewDialog from '@/components/PromptPreviewDialog';
import PromptVersionHistory from '@/components/PromptVersionHistory';
import ABTestStats from '@/components/ABTestStats';
import LogoUpload from '@/components/LogoUpload';
import GoogleEnrichment from '@/components/GoogleEnrichment';
import ContextSources from '@/components/ContextSources';
import AgentWizard from '@/components/AgentWizard';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function AgentSettings() {
  const navigate = useNavigate();
  const { agentId } = useParams();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    fetchAgent();
  }, [agentId]);

  const fetchAgent = async () => {
    try {
      const response = await api.get(`/agents/${agentId}`);
      setAgent(response.data);
    } catch (error) {
      toast.error('Failed to fetch agent');
      navigate('/dashboard');
    }
  };

  const handleChange = (field, value) => {
    setAgent({ ...agent, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.put(`/agents/${agentId}`, {
        name: agent.name,
        context_text: agent.context_text,
        escalation_text: agent.escalation_text,
        response_style: agent.response_style,
        preferred_role: agent.preferred_role,
        business_summary: agent.business_summary,
        out_of_scope_message: agent.out_of_scope_message,
        prompt_variant: agent.prompt_variant,
        enable_google_enrichment: agent.enable_google_enrichment,
        business_name_public: agent.business_name_public,
        business_address_query: agent.business_address_query,
      });
      toast.success('Settings updated successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  if (!agent) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="rounded-full"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Settings className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-heading font-bold text-foreground" data-testid="settings-title">Agent Settings</h1>
                <p className="text-sm text-muted-foreground">{agent.name}</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowWizard(true)}
              className="rounded-full"
              data-testid="launch-wizard-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Setup Wizard
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Action Buttons */}
        <div className="flex items-center space-x-2 mb-6">
          <PromptPreviewDialog agentId={agentId} />
          <PromptVersionHistory agentId={agentId} />
          <ABTestStats agentId={agentId} />
        </div>

        <Card className="border-border shadow-lg" data-testid="settings-form">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">Configuration</CardTitle>
            <CardDescription>Customize how your agent responds to users</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">Agent Name *</Label>
                <Input
                  id="name"
                  name="name"
                  placeholder="e.g., Customer Support Agent"
                  value={agent.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="h-11 rounded-xl"
                  required
                  data-testid="agent-name-input"
                />
                <p className="text-xs text-muted-foreground">This name is displayed to users in the chat interface</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="preferred_role" className="text-sm font-medium">Preferred Agent Role</Label>
                <Input
                  id="preferred_role"
                  name="preferred_role"
                  placeholder="e.g., Customer Support Specialist, Sales Representative"
                  value={agent.preferred_role || ''}
                  onChange={(e) => handleChange('preferred_role', e.target.value)}
                  className="h-11 rounded-xl"
                  data-testid="preferred-role-input"
                />
                <p className="text-xs text-muted-foreground">Sets the agent's persona (e.g., "You are a Customer Support Specialist...")</p>
              </div>

              <LogoUpload 
                agentId={agentId} 
                currentLogo={agent.logo}
                onLogoUpdated={(newLogo) => setAgent({ ...agent, logo: newLogo })}
              />

              <div className="border-t pt-6">
                <h3 className="text-lg font-heading font-semibold mb-4">Google Business Profile</h3>
                <GoogleEnrichment
                  agentId={agentId}
                  agent={agent}
                  onUpdate={(updates) => setAgent({ ...agent, ...updates })}
                />
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-heading font-semibold mb-2">Context Sources</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Add additional context from text, websites, or documents. Enable/disable each source individually.
                </p>
                <ContextSources agentId={agentId} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="business_summary" className="text-sm font-medium">Business Summary</Label>
                <Textarea
                  id="business_summary"
                  name="business_summary"
                  placeholder="Describe your business, what you do, and what you offer..."
                  value={agent.business_summary || ''}
                  onChange={(e) => handleChange('business_summary', e.target.value)}
                  className="min-h-32 rounded-xl"
                  data-testid="business-summary-input"
                />
                <p className="text-xs text-muted-foreground">Helps the AI understand your business context</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="out_of_scope_message" className="text-sm font-medium">Out of Scope Message</Label>
                <Textarea
                  id="out_of_scope_message"
                  name="out_of_scope_message"
                  placeholder="Message shown when user asks about topics outside your business scope..."
                  value={agent.out_of_scope_message || ''}
                  onChange={(e) => handleChange('out_of_scope_message', e.target.value)}
                  className="min-h-24 rounded-xl"
                  data-testid="out-of-scope-input"
                />
                <p className="text-xs text-muted-foreground">Use {`{TENANT_NAME}`} to insert your business name</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="context_text" className="text-sm font-medium">Context / System Prompt *</Label>
                <Textarea
                  id="context_text"
                  name="context_text"
                  placeholder="Describe the agent's role and behavior..."
                  value={agent.context_text}
                  onChange={(e) => handleChange('context_text', e.target.value)}
                  className="min-h-32 rounded-xl"
                  required
                  data-testid="context-text-input"
                />
                <p className="text-xs text-muted-foreground">This defines how the agent behaves and responds</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="escalation_text" className="text-sm font-medium">Escalation Text</Label>
                <Textarea
                  id="escalation_text"
                  name="escalation_text"
                  placeholder="Instructions for escalating to human support..."
                  value={agent.escalation_text}
                  onChange={(e) => handleChange('escalation_text', e.target.value)}
                  className="min-h-24 rounded-xl"
                  data-testid="escalation-text-input"
                />
                <p className="text-xs text-muted-foreground">Guidance for when users need human assistance</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="response_style" className="text-sm font-medium">Response Style *</Label>
                <Select
                  value={agent.response_style}
                  onValueChange={(value) => handleChange('response_style', value)}
                >
                  <SelectTrigger className="h-11 rounded-xl" data-testid="response-style-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="short" data-testid="style-short">Short (Brief, concise responses)</SelectItem>
                    <SelectItem value="normal" data-testid="style-normal">Normal (Detailed, comprehensive responses)</SelectItem>
                    <SelectItem value="fun" data-testid="style-fun">Fun (Engaging with emojis, links & images)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  "Fun" style includes emojis and automatically shows relevant links and images from your context sources.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="prompt_variant" className="text-sm font-medium">Prompt Variant (A/B Testing) *</Label>
                <Select
                  value={agent.prompt_variant || 'A'}
                  onValueChange={(value) => handleChange('prompt_variant', value)}
                >
                  <SelectTrigger className="h-11 rounded-xl" data-testid="prompt-variant-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A" data-testid="variant-a">Variant A (Guardrails First - Rules, then Context)</SelectItem>
                    <SelectItem value="B" data-testid="variant-b">Variant B (Context First - Role, then Rules)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Test different prompt orderings to optimize response quality</p>
              </div>

              <div className="flex space-x-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="flex-1 rounded-full"
                  data-testid="cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 rounded-full shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all hover:-translate-y-0.5"
                  disabled={loading}
                  data-testid="submit-button"
                >
                  {loading ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>

      {/* Agent Setup Wizard */}
      <AgentWizard
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        agentId={agentId}
        onComplete={(id, finished) => {
          if (finished) {
            fetchAgent(); // Refresh agent data
          }
        }}
      />
    </div>
  );
}
